<template> 
  <div v-if="loadingMethods" style="height: 300px;"></div>
  <div v-else>
    <v-card v-if="!selectedMethod" flat class="payment-method-page mx-auto d-flex flex-column">
      <span class="pageTitle">{{$t('payment_method')}}</span>
      <v-list class="pt-0 pb-4" min-height="200">

        <template v-for="(item, index) in paymentMethods">

          <v-list-item :key="index" class='methodItem pr-0 pl-5' @click='selectMethod(item)'>
            <v-list-item-content v-if="isCardMethod(item)">
              <div class="d-flex">
                <img class="mr-1" src="@/assets/Visa.svg"/>
                <img class="mr-1" src="@/assets/Mastercard.svg"/>
                <img src="@/assets/CB.svg"/>
              </div>
            </v-list-item-content>

            <v-list-item-content v-else-if="isPISPMethod(item)">
              <div class="d-flex align-center">
                <img src="@/assets/stopwatch-outline.svg"/>
                <v-list-item-subtitle>{{ item.paymentMethodLabel }} </v-list-item-subtitle>
                <v-tooltip class="tooltip-pisp" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon size="16"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{'$infocircleoutline'}}
                    </v-icon>
                  </template>
                  <span>{{$t('tooltip_pisp')}}</span>
                </v-tooltip>
              </div>
            </v-list-item-content>

            <v-list-item-content v-else>
              <div class="d-flex align-center">
                <img src="@/assets/SCT.svg"/>
                <v-list-item-subtitle>{{ item.paymentMethodLabel }} </v-list-item-subtitle>
              </div>              
            </v-list-item-content>

            <v-list-item-action class="my-auto ml-1">
              <img class="selectPaymentButton" src="@/assets/Selection.svg"/>
            </v-list-item-action>

          </v-list-item>
        </template>
      </v-list>
      <v-spacer/>
    </v-card>

    <CardSelection v-else-if="isCardMethod(selectedMethod)" :mkpData="paymentMethodResponse" :cards="selectedMethod" @select="selectAlias" @abort="reset"/>
    <AccountSelectionPISP v-else-if="isPISPMethod(selectedMethod)" :cards="selectedMethod" :termsUrl="termsUrl" @select="selectAlias" @abort="reset"/>
    <AccountSelectionSDD v-else-if="isSDDMethod(selectedMethod)" :cards="selectedMethod" @select="selectAlias" @abort="reset"/>
  </div>
</template>

<script>
import { VCard, VSpacer, VListItemContent, VListItemAction, VListItemSubtitle, VListItem, VList, VTooltip, VIcon } from 'vuetify/lib'
import RequestHandler from '@/views/payment/mixins'
import testData from '@/views/payment/views/desktop/testData'
import CardSelection from '@/views/payment/views/desktop/methodSelection/CardSelection'
import AccountSelectionPISP from '@/views/payment/views/desktop/methodSelection/AccountSelection_PISP'
import AccountSelectionSDD from '@/views/payment/views/desktop/methodSelection/AccountSelection_SDD'
import valueMappings from '@/mixins/valueMappings'
import localisation from '@/mixins/localisation'
import {mapMutations} from 'vuex'

export default {
  name: 'PaymentMethod',
  components: { 
    VCard, 
    VSpacer, 
    VListItemContent, 
    VListItemAction, 
    VListItemSubtitle, 
    VListItem, 
    VList, 
    VTooltip,
    VIcon,
    CardSelection,
    AccountSelectionPISP,
    AccountSelectionSDD
  },
  mixins: [RequestHandler, valueMappings, localisation],
  filters: {
    upperCase (val) {
      return val.toUpperCase()
    }
  },
  props: {
    origin: {
      type: String,
      default: ''
    }
  },
  created: function () {
    this.startLoader()
  },
  methods: {
    ...mapMutations(['SET_singlePaymentMethod']),
    stopLoader () {
      this.$emit('stopLoader')
      this.loadingMethods = false
    },
    startLoader () {
      this.$emit('startLoader')
    },
    selectAlias (data) {
      let registerAlias = data.registerAlias === true || data.registerAlias === "1"
      this.$emit('methodSelected', { methodTypeId: data.item.paymentMethodTypeId, methodId: data.item.paymentMethodId, aliasId: data.aliasId, aliasStep: data.item.aliasStep, registerAlias: registerAlias, aliasLabel: data.aliasLabel, startNeedsInput: data.startNeedsInput, mkpData: this.paymentMethodResponse, doNotAddDetails: data.doNotAddDetails })
    },
    selectMethod (item) {
      if (this.needsInput(item)) this.selectedMethod = item
      else this.$emit('methodSelected', { methodTypeId: item.paymentMethodTypeId, methodId: item.paymentMethodId, mkpData: this.paymentMethodResponse })
    },
    isCardMethod (method) {
      return this.isCard(method.paymentMethodTypeId)
    },
    isPISPMethod (method) {
      return this.isPISP(method.paymentMethodTypeId)
    },
    isSDDMethod (method) {
      return method.aliasList && method.aliasList.length && this.isSDD(method.paymentMethodTypeId)
    },
    needsInput (method) {
      return this.isCard(method.paymentMethodTypeId) || this.isPISP(method.paymentMethodTypeId) || this.isSDDMethod(method)
    },
    reset () {
      this.reload++
      this.selectedMethod = null
    }
  },
  asyncComputed: {
    paymentMethods: {
      get () {
        this.reloadLabel = this.reload
        return this.callPaymentMethods().then(rep => {
          if (!rep) throw new Error(this.$t('error_fetch_paymentmethods'))
          const content = rep.content
          const noError = content.statusCode === '0'
          if (noError) {
            this.paymentMethodResponse = content
            return content.paymentMethodList
          } 
          else if (process.env.NODE_ENV !== 'production'){            
            this.paymentMethodResponse = testData.paymentMethodResponse
            return testData.paymentMethods
          } 
        })
        .catch(err => {
          this.stopLoader()
          this.setFatalError({ description: this.$t(err.message)})       
        })
      },
      default: []
    }
  }, 
  computed: {
    termsUrl: function () {
      return this.paymentMethodResponse ? this.paymentMethodResponse['GCUURL'] : ''
    }
  },
  watch: {
    paymentMethods (newValue) {
      this.stopLoader()
      let singlePaymentMethod = newValue && newValue.length === 1
      this.SET_singlePaymentMethod(singlePaymentMethod)
      if (singlePaymentMethod) this.selectMethod(newValue[0])
    }
  },
  data () {
    return {
      loadingMethods: true,
      selectedMethod: null,
      paymentMethodResponse: null,
      reload: 0, // dummy variable that serves to reload the paymentMethods when resetting
      reloadLabel: ''  // dummy variable that serves to reload the paymentMethods when resetting
    }
  }
}
</script>
